import { useState, useCallback, useRef, ChangeEvent, useEffect } from 'react'
import { usePlatform } from './contexts/PlatformContext'
import { Capacitor } from '@capacitor/core'
import { SendIntent } from 'send-intent'
import { useDropzone } from 'react-dropzone'
import { Upload, FileText, Download, Loader2, CheckCircle, AlertCircle, MessageSquare, Film, Info, FileArchive, ChevronDown, ChevronUp, Eye, Settings, Users } from 'lucide-react'
import axios from 'axios'

// In Capacitor (native app), relative URLs hit the WebView bundle, not the backend.
// Use the Tailscale server address directly.
const API_BASE = Capacitor.isNativePlatform() ? 'https://chatarchiv.de' : ''
import './App.css'
import { PreviewOptions, WhatsAppMessage, ChatStats, ParseQuality, defaultOptions } from './types/preview'
import { ChatPreview } from './components/ChatPreview'
import { ParseQualityNotice } from './components/ParseQualityNotice'
import { PreviewSidebar } from './components/PreviewSidebar'
import { PaymentSuccess } from './components/PaymentSuccess'
import { LandingHero } from './components/LandingHero'
import { LandingTrustBar } from './components/LandingTrustBar'
import { LandingHowItWorks } from './components/LandingHowItWorks'
import { LandingWhy } from './components/LandingWhy'
import { LandingBeforeAfter } from './components/LandingBeforeAfter'
import { LandingFeatures } from './components/LandingFeatures'
import { LandingPricing } from './components/LandingPricing'
import { LandingFAQ } from './components/LandingFAQ'
import { LandingFooter } from './components/LandingFooter'
import { MobileOptionsSheet } from './components/MobileOptionsSheet'
import { CodeEntry, HandoffResponse } from './components/CodeEntry'
import { CapacitorShell } from './components/capacitor/CapacitorShell'
import { useBilling } from './hooks/useBilling'
import './components/ChatPreview.css'
import './components/CodeEntry.css'


interface UploadStatus {
  jobId: string | null
  status: 'idle' | 'uploading' | 'processing' | 'completed' | 'error'
  progress: number
  message: string
  result?: {
    pdf_path: string
    file_size: number
    message_count: number
    participants: string[]
    has_media?: boolean
    media_count?: number
  }
  error?: string
}

type MediaMode = 'thumbnail' | 'link' | 'both' | 'text_only'

function App() {
  const [uploadStatus, setUploadStatus] = useState<UploadStatus>({
    jobId: null,
    status: 'idle',
    progress: 0,
    message: ''
  })
  
  // Share Target Detection (TWA / Web Share Target API)
  useEffect(() => {
    const handleShare = async () => {
      const urlParams = new URL(window.location.href).searchParams;
      const isShare = urlParams.get('share-target') === 'true';
      if (!isShare) return;

      // Sofort Loading-State zeigen
      window.history.replaceState({}, document.title, '/');
      setUploadStatus({
        jobId: null,
        status: 'uploading',
        progress: 10,
        message: 'Geteilte Datei wird verarbeitet...'
      });

      try {
        const cache = await caches.open('chatarxiv-share-cache');
        const response = await cache.match('/shared-file');

        if (!response) {
          // Cache miss — Service Worker hat die Datei nicht gespeichert
          setUploadStatus({
            jobId: null,
            status: 'error',
            progress: 0,
            message: 'Datei nicht empfangen',
            error: 'Die geteilte Datei konnte nicht geladen werden. Bitte teile den Chat-Export erneut oder lade die Datei direkt hoch.'
          });
          return;
        }

        const blob = await response.blob();
        await cache.delete('/shared-file');

        // Detect ZIP by magic bytes (PK = 0x50 0x4B)
        const header = await blob.slice(0, 4).arrayBuffer();
        const bytes = new Uint8Array(header);
        const isZip = bytes[0] === 0x50 && bytes[1] === 0x4B;

        // Detect WhatsApp's email intro text (Android shares this instead of the actual file)
        if (!isZip && blob.size < 500) {
          const text = await blob.text();
          if (text.includes('Chatverlauf') || text.includes('WhatsApp')) {
            setUploadStatus({
              jobId: null,
              status: 'error',
              progress: 0,
              message: 'Falsche Datei empfangen',
              error: 'Android hat nur den Begleittext geteilt, nicht die Chat-Datei. Bitte speichere den Export zuerst in der Dateien-App und teile von dort.'
            });
            return;
          }
        }

        const filename = isZip ? 'whatsapp_share.zip' : 'whatsapp_share.txt';
        const mimeType = isZip ? 'application/zip' : 'text/plain';
        const file = new File([blob], filename, { type: mimeType });

        if (isZip) {
          setZipFile(file);
          setTxtFile(null);
        } else {
          setTxtFile(file);
          setZipFile(null);
        }

        uploadFiles(isZip ? file : null, isZip ? null : file);
      } catch (err) {
        console.error('Failed to retrieve shared file:', err);
        setUploadStatus({
          jobId: null,
          status: 'error',
          progress: 0,
          message: 'Fehler beim Verarbeiten der geteilten Datei',
          error: 'Bitte versuche es erneut oder lade die Datei direkt hoch.'
        });
      }
    };

    handleShare();
  }, []);

  // Handle return from Stripe Checkout
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const payment = params.get('payment')
    const renderJobId = params.get('render_job_id') || localStorage.getItem('pending_render_job_id')

    if (payment === 'success' && renderJobId) {
      window.history.replaceState({}, document.title, '/')
      setPaymentSuccessJobId(renderJobId)
    } else if (payment === 'cancelled') {
      // Job wiederherstellen statt den Nutzer zum Neu-Upload zu zwingen
      const cancelledJobId = params.get('job_id') || localStorage.getItem('pending_parent_job_id')
      window.history.replaceState({}, document.title, '/')
      if (cancelledJobId) restoreJobAfterCancel(cancelledJobId)
    }
  }, []);

  // Nach Stripe-Abbruch: Upload-Zustand + Preview aus der bestehenden Job-ID wiederherstellen
  const restoreJobAfterCancel = async (jobId: string) => {
    try {
      const statusResp = await axios.get(`${API_BASE}/api/upload/status/${jobId}`)
      if (statusResp.data?.status !== 'completed') return
      setUploadStatus({
        jobId,
        status: 'completed',
        progress: 100,
        message: 'Zahlung abgebrochen — dein Chat ist weiterhin geladen.',
        result: statusResp.data.result,
      })
      const previewResp = await axios.get(`${API_BASE}/api/upload/preview/${jobId}`)
      if (previewResp.data?.messages) {
        setPreviewMessages(previewResp.data.messages)
        setPreviewStats(previewResp.data.stats)
        setPreviewQuality(previewResp.data.quality ?? null)
      }
    } catch {
      // Job existiert nicht mehr (abgelaufen) — Startzustand ist dann korrekt
    }
  }

  // Files
  const [zipFile, setZipFile] = useState<File | null>(null)
  const [txtFile, setTxtFile] = useState<File | null>(null)
  
  // Media settings
  const [mediaMode, setMediaMode] = useState<MediaMode>('both')
  const [showAdvanced, setShowAdvanced] = useState(false)

  // Live Preview State
  const [previewOptions, setPreviewOptions] = useState<PreviewOptions>(defaultOptions)
  const [previewMessages, setPreviewMessages] = useState<WhatsAppMessage[]>([])
  const [previewStats, setPreviewStats] = useState<ChatStats>({
    totalMessages: 0,
    participants: [],
    exporteur: '',
    mediaCount: 0,
    dateRange: { start: new Date(), end: new Date() }
  })
  const [previewQuality, setPreviewQuality] = useState<ParseQuality | null>(null)

  const [showPreview, setShowPreview] = useState(false)
  const [isPreviewLoading, setIsPreviewLoading] = useState(false)
  const [isRendering, setIsRendering] = useState(false)
  const [paymentSuccessJobId, setPaymentSuccessJobId] = useState<string | null>(null)
  const [widerrufsCheckbox, setWiderrufsCheckbox] = useState(false)
  const [agbCheckbox, setAgbCheckbox] = useState(false)
  const [mobileSheetOpen, setMobileSheetOpen] = useState(false)
  const [checkboxError, setCheckboxError] = useState(false)
  const [isGeneratingFreePdf, setIsGeneratingFreePdf] = useState(false)
  const [handoffSource, setHandoffSource] = useState(false)
  const [handoffExpiresIn, setHandoffExpiresIn] = useState<number | null>(null)

  const { products: billingProducts, loading: billingLoading, purchase: googlePlayPurchase } = useBilling()

  const platform = usePlatform()
  const isMobile = platform === 'mobile-web'

  // Capacitor: receive WhatsApp ZIP via Android Share Intent
  useEffect(() => {
    if (platform !== 'capacitor') return

    const handleNativeShareUri = async (uri: string, title: string) => {
      console.log('handleNativeShareUri:', uri, title)
      setUploadStatus({ jobId: null, status: 'uploading', progress: 10, message: 'Datei wird empfangen...' })

      try {
        // content:// and file:// URIs need convertFileSrc() to be fetchable from WebView
        const needsConvert = uri.startsWith('content://') || uri.startsWith('file://')
        const fetchUrl = needsConvert ? Capacitor.convertFileSrc(uri) : uri
        console.log('fetchUrl:', fetchUrl)

        const response = await fetch(fetchUrl)
        if (!response.ok) throw new Error(`HTTP ${response.status} for ${fetchUrl}`)
        const blob = await response.blob()
        console.log('blob size:', blob.size)

        const filename = title?.endsWith('.zip') ? title : 'whatsapp_export.zip'
        const file = new File([blob], filename, { type: 'application/zip' })

        setZipFile(file)
        uploadFiles(file, null)
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err)
        console.error('Capacitor share intent error:', err)
        setUploadStatus({ jobId: null, status: 'error', progress: 0, message: `Fehler: ${msg}` })
      }
    }

    // Listen for the native event dispatched by MainActivity.onNewIntent (app already running)
    const nativeListener = (e: Event) => {
      const { uri, title } = (e as CustomEvent).detail
      handleNativeShareUri(uri, title)
    }
    window.addEventListener('nativeShareIntent', nativeListener)

    // Cold-start fallback: app launched fresh via share intent — send-intent plugin works here
    // because getIntent() in onCreate already has ACTION_SEND
    const coldStartCheck = async () => {
      try {
        const result = await SendIntent.checkSendIntentReceived()
        if (result?.url) {
          handleNativeShareUri(result.url, result.title ?? '')
        }
      } catch {
        // No share intent on cold start — ignore
      }
    }
    coldStartCheck()

    return () => window.removeEventListener('nativeShareIntent', nativeListener)
  }, [platform]) // eslint-disable-line react-hooks/exhaustive-deps

  // Refs for file inputs (mobile compatible)
  const zipInputRef = useRef<HTMLInputElement>(null)
  const txtInputRef = useRef<HTMLInputElement>(null)

  // ZIP Dropzone (primary) - for desktop drag & drop
  const onZipDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0]
    if (file && file.name.toLowerCase().endsWith('.zip')) {
      setZipFile(file)
      setTxtFile(null)
    }
  }, [])

  const { getRootProps: getZipRootProps, getInputProps: getZipInputProps, isDragActive: isZipDragActive } = useDropzone({
    onDrop: onZipDrop,
    maxFiles: 1,
    noClick: true // Disable click, use custom button instead
  })

  // TXT Dropzone (fallback) - for desktop drag & drop
  const onTxtDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0]
    if (file && file.name.toLowerCase().endsWith('.txt')) {
      setTxtFile(file)
      setZipFile(null)
    }
  }, [])

  const { getRootProps: getTxtRootProps, getInputProps: getTxtInputProps, isDragActive: isTxtDragActive } = useDropzone({
    onDrop: onTxtDrop,
    maxFiles: 1,
    noClick: true // Disable click, use custom button instead
  })

  // Mobile-friendly file selection
  const handleHandoffLoaded = async (jobData: HandoffResponse) => {
    setUploadStatus({
      jobId: jobData.job_id,
      status: 'completed',
      progress: 100,
      message: 'Chat von App geladen!',
      result: jobData.result,
    })
    setHandoffSource(true)
    if (jobData.expires_in_seconds) {
      setHandoffExpiresIn(jobData.expires_in_seconds)
    }
    // Load preview messages — same as pollStatus does after upload completes
    try {
      const previewResp = await axios.get(`/api/upload/preview/${jobData.job_id}`)
      if (previewResp.data?.messages) {
        setPreviewMessages(previewResp.data.messages)
        setPreviewStats(previewResp.data.stats)
        setPreviewQuality(previewResp.data.quality ?? null)
      }
    } catch (e) {
      console.error('Handoff preview load failed:', e)
    }
  }

  const handleZipSelect = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    console.log('ZIP selected:', file?.name, 'Type:', file?.type, 'Size:', file?.size)
    if (file) {
      setZipFile(file)
      setTxtFile(null)
    }
  }

  const handleTxtSelect = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    console.log('TXT selected:', file?.name, 'Type:', file?.type, 'Size:', file?.size)
    if (file) {
      setTxtFile(file)
      setZipFile(null)
    }
  }

  const uploadFiles = async (zip: File | null, txt: File | null) => {
    if (!zip && !txt) return

    setUploadStatus({
      jobId: null,
      status: 'uploading',
      progress: 25,
      message: zip ? 'ZIP wird hochgeladen...' : 'Chat wird hochgeladen...'
    })

    const formData = new FormData()

    if (zip) {
      formData.append('media_zip', zip)
      formData.append('upload_mode', 'zip')
    } else {
      formData.append('chat_file', txt!)
      formData.append('upload_mode', 'txt')
    }
    
    formData.append('output_format', 'whatsapp')
    formData.append('media_mode', mediaMode)

    try {
      const response = await axios.post(`${API_BASE}/api/upload/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 10 * 60 * 1000,
        onUploadProgress: (e) => {
          if (e.total) {
            // Upload belegt 5–70 % des Balkens, danach Verarbeitung
            const pct = 5 + Math.round((e.loaded / e.total) * 65)
            setUploadStatus(prev => ({ ...prev, progress: pct }))
          }
        },
      })

      const { job_id, status } = response.data

      setUploadStatus(prev => ({
        ...prev,
        jobId: job_id,
        status: status === 'queued' ? 'processing' : status,
        progress: 75,
        message: 'PDF wird generiert...'
      }))

      pollStatus(job_id)

    } catch (error: any) {
      setUploadStatus({
        jobId: null,
        status: 'error',
        progress: 0,
        message: 'Upload fehlgeschlagen',
        error: error.response?.data?.detail || error.message
      })
    }
  }

  const handleUpload = () => uploadFiles(zipFile, txtFile)

  const pollStatus = async (jobId: string) => {
    let attempts = 0
    const MAX_ATTEMPTS = 150 // 150 × 2 s = 5 Minuten, danach Fehler statt Endlosschleife

    const checkStatus = async () => {
      if (++attempts > MAX_ATTEMPTS) {
        setUploadStatus({
          jobId,
          status: 'error',
          progress: 0,
          message: 'Verarbeitung dauert ungewöhnlich lange',
          error: 'Zeitüberschreitung — bitte versuche es erneut oder kontaktiere den Support.'
        })
        return
      }
      try {
        const response = await axios.get(`${API_BASE}/api/upload/status/${jobId}`)
        const { status, result, error } = response.data

        if (status === 'completed') {
          setUploadStatus({
            jobId,
            status: 'completed',
            progress: 100,
            message: 'PDF erstellt!',
            result
          })
          // Auto-load preview data (needed for Capacitor flow where no button triggers it)
          try {
            const previewResp = await axios.get(`${API_BASE}/api/upload/preview/${jobId}`)
            if (previewResp.data?.messages) {
              // Prefix relative media URLs with API_BASE for Capacitor (native app)
              const fixUrl = (url?: string) =>
                url && API_BASE && url.startsWith('/') ? `${API_BASE}${url}` : url
              const msgs = previewResp.data.messages.map((m: any) => ({
                ...m,
                media_url: fixUrl(m.media_url),
                media_thumb_url: fixUrl(m.media_thumb_url),
              }))
              setPreviewMessages(msgs)
              setPreviewStats(previewResp.data.stats)
              setPreviewQuality(previewResp.data.quality ?? null)
            }
          } catch (e) {
            console.error('Preview auto-load failed:', e)
          }
        } else if (status === 'failed') {
          setUploadStatus({
            jobId,
            status: 'error',
            progress: 0,
            message: 'Verarbeitung fehlgeschlagen',
            error
          })
        } else {
          setUploadStatus(prev => ({
            ...prev,
            jobId,
            status: 'processing',
            progress: 85,
            message: 'PDF wird generiert...'
          }))
          setTimeout(checkStatus, 2000)
        }
      } catch (error: any) {
        setUploadStatus({
          jobId,
          status: 'error',
          progress: 0,
          message: 'Status-Prüfung fehlgeschlagen',
          error: error.message
        })
      }
    }

    checkStatus()
  }

  const handleFreePdfDownload = async () => {
    if (!uploadStatus.jobId) return
    setIsGeneratingFreePdf(true)
    try {
      // Preview-Optionen mitschicken, damit das Gratis-PDF der Vorschau entspricht
      const serializedOptions = {
        ...previewOptions,
        dateRange: {
          from: previewOptions.dateRange.from?.toISOString() ?? null,
          to: previewOptions.dateRange.to?.toISOString() ?? null,
        }
      }
      await axios.post(`${API_BASE}/api/upload/free-pdf/${uploadStatus.jobId}`, serializedOptions)
      // Poll until ready
      for (let i = 0; i < 30; i++) {
        await new Promise(r => setTimeout(r, 2000))
        const { data } = await axios.get(`${API_BASE}/api/upload/free-pdf/${uploadStatus.jobId}/status`)
        if (data.status === 'completed') {
          // Download per Anker statt window.open — window.open nach await
          // wird von Popup-Blockern (iOS Safari) verschluckt.
          const a = document.createElement('a')
          a.href = `${API_BASE}/api/upload/free-pdf/${uploadStatus.jobId}/download`
          a.download = 'chatarchiv-vorschau.pdf'
          document.body.appendChild(a)
          a.click()
          a.remove()
          return
        }
        if (data.status === 'failed') throw new Error(data.error || 'PDF-Generierung fehlgeschlagen')
      }
      throw new Error('Timeout')
    } catch (e: any) {
      alert('Fehler beim Erstellen des kostenlosen PDFs: ' + (e.response?.data?.detail || e.message))
    } finally {
      setIsGeneratingFreePdf(false)
    }
  }

  const startCheckout = async () => {
    if (!uploadStatus.jobId) return
    if (!agbCheckbox || !widerrufsCheckbox) {
      setCheckboxError(true)
      return
    }
    setIsRendering(true)

    const serializedOptions = {
      ...previewOptions,
      dateRange: {
        from: previewOptions.dateRange.from?.toISOString() ?? null,
        to: previewOptions.dateRange.to?.toISOString() ?? null,
      }
    }

    try {
      const resp = await axios.post(`${API_BASE}/api/payments/create-checkout`, {
        job_id: uploadStatus.jobId,
        preview_options: serializedOptions,
        consent: {
          // Echte Checkbox-Werte — das Backend lehnt false ab.
          agb_accepted: agbCheckbox,
          widerruf_accepted: widerrufsCheckbox,
          agb_version: '2026-04',
          accepted_at: new Date().toISOString(),
        },
      })
      const { checkout_url, render_job_id } = resp.data

      // Store render_job_id so we can poll after returning from Stripe
      localStorage.setItem('pending_render_job_id', render_job_id)
      localStorage.setItem('pending_parent_job_id', uploadStatus.jobId)

      // Open Stripe in external browser (required for TWA Play Store compliance)
      window.location.href = checkout_url
    } catch (e: any) {
      console.error('Checkout error:', e)
      alert('Zahlung konnte nicht gestartet werden: ' + (e.response?.data?.detail || e.message))
      setIsRendering(false)
    }
  }


  const startGooglePlayPurchase = async (productId: string) => {
    if (!uploadStatus.jobId) return
    setIsRendering(true)
    try {
      const renderJobId = await googlePlayPurchase(productId, uploadStatus.jobId, previewOptions)
      setPaymentSuccessJobId(renderJobId)
    } catch (e: any) {
      if (e.message !== 'USER_CANCELED') {
        alert('Kauf fehlgeschlagen: ' + (e.response?.data?.detail ?? e.message))
      }
    } finally {
      setIsRendering(false)
    }
  }

  const resetUpload = () => {
    setUploadStatus({ jobId: null, status: 'idle', progress: 0, message: '' })
    setZipFile(null)
    setTxtFile(null)
    // Reset file inputs
    if (zipInputRef.current) zipInputRef.current.value = ''
    if (txtInputRef.current) txtInputRef.current.value = ''
    setHandoffSource(false)
    setHandoffExpiresIn(null)
    setShowPreview(false)
    setPreviewMessages([])
    setIsRendering(false)
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  // Load preview data from backend
  const loadPreview = async () => {
    if (!uploadStatus.jobId) return
    
    setIsPreviewLoading(true)
    try {
      // Fetch preview data from backend
      const response = await axios.get(`${API_BASE}/api/upload/preview/${uploadStatus.jobId}`)
      
      if (response.data) {
        setPreviewMessages(response.data.messages)
        setPreviewStats(response.data.stats)
        setPreviewQuality(response.data.quality ?? null)
        
        // Don't override selectedSenders — default [] means show all senders incl. System
        
        setShowPreview(true)
      }
    } catch (error: any) {
      console.error('Preview load error:', error)
      // Ehrlicher Fehler statt leerer Vorschau mit aktivem Kaufbutton
      setUploadStatus(prev => ({
        ...prev,
        status: 'error',
        message: 'Vorschau konnte nicht geladen werden',
        error: error.response?.data?.detail || error.message,
      }))
    } finally {
      setIsPreviewLoading(false)
    }
  }

  const scrollToUpload = () => {
    document.getElementById('upload')?.scrollIntoView({ behavior: 'smooth' })
  }

  return (
    <div className="app">
      {/* Sticky Navbar — hidden in Capacitor (native app has its own shell) */}
      <header className="app-nav" style={platform === 'capacitor' ? { display: 'none' } : undefined}>
        <a className="nav-logo" href="/">
          <MessageSquare className="nav-logo-icon" />
          <span className="nav-logo-text">ChatArchiv.de</span>
        </a>
        {uploadStatus.status === 'idle' && (
          <nav className="nav-links">
            <a className="nav-link" href="#so-funktioniert">So funktioniert's</a>
            <a className="nav-link" href="#funktionen">Funktionen</a>
            <a className="nav-link" href="#preise">Preise</a>
            <a className="nav-link" href="#faq">FAQ</a>
          </nav>
        )}
        {uploadStatus.status === 'idle' && (
          <button className="nav-cta" onClick={scrollToUpload}>
            Jetzt starten →
          </button>
        )}
      </header>

      {platform === 'capacitor' ? (
        <CapacitorShell
          uploadStatus={uploadStatus}
          previewMessages={previewMessages}
          previewStats={previewStats}
          previewQuality={previewQuality}
          previewOptions={previewOptions}
          setPreviewOptions={setPreviewOptions}
          onUpload={(file) =>
            file.name.toLowerCase().endsWith('.txt')
              ? uploadFiles(null, file)
              : uploadFiles(file, null)
          }
          onReset={resetUpload}
          onPurchase={startGooglePlayPurchase}
          isRendering={isRendering || billingLoading}
          paymentSuccessJobId={paymentSuccessJobId}
          billingProducts={billingProducts}
        />
      ) : (
        <>
      {/* Landing Page (only when idle) */}
      {uploadStatus.status === 'idle' && (
        <>
          <LandingHero onStart={scrollToUpload} />
          <LandingTrustBar />
          <LandingHowItWorks />
          <LandingWhy />
          <LandingBeforeAfter onStart={scrollToUpload} />
        </>
      )}

      {/* Upload section (anchor for scroll, always in idle) */}
      {uploadStatus.status === 'idle' ? (
        <section id="upload" className="upload-section">
          <div className="upload-section-header">
            <h2 className="upload-section-title">Chat hochladen</h2>
            <p className="upload-section-subtitle">ZIP-Datei auswählen — kostenloses PDF sofort verfügbar</p>
          </div>

          {/* Hidden native file inputs for mobile */}
          <input
            ref={zipInputRef}
            type="file"
            accept=".zip"
            onChange={handleZipSelect}
            style={{ display: 'none' }}
          />
          <input
            ref={txtInputRef}
            type="file"
            accept=".txt"
            onChange={handleTxtSelect}
            style={{ display: 'none' }}
          />

          <div className={`upload-grid ${platform === 'desktop' ? 'upload-grid--two-col' : ''}`}>
            <div className="upload-grid__dropzone">
              {/* ZIP Upload Zone */}
              <div {...getZipRootProps()} className={`dropzone primary ${isZipDragActive ? 'active' : ''} ${zipFile ? 'has-file' : ''}`}>
                <input {...getZipInputProps()} />
                <span className="dropzone-corner-badge">Empfohlen</span>

                {zipFile ? (
                  <div className="file-preview">
                    <FileArchive size={36} className="file-icon zip" />
                    <p className="file-name">{zipFile.name}</p>
                    <p className="file-size">{formatFileSize(zipFile.size)}</p>
                    <button className="change-file" onClick={(e) => { e.stopPropagation(); setZipFile(null); }}>Ändern</button>
                  </div>
                ) : (
                  <div className="dropzone-content">
                    <div className="dropzone-icon-wrap">
                      <FileArchive size={32} />
                    </div>
                    <p className="dropzone-text">ZIP-Export hochladen</p>
                    <p className="dropzone-subtext">Chat-Verlauf inkl. aller Medien</p>
                    <button
                      type="button"
                      className="select-file-btn"
                      onClick={() => zipInputRef.current?.click()}
                    >
                      Datei auswählen
                    </button>
                    <p className="dropzone-drag-hint">oder hierhin ziehen</p>
                  </div>
                )}
              </div>

              {!zipFile && (
                <div className="or-divider"><span>oder nur Text hochladen</span></div>
              )}

              {/* TXT Upload Zone */}
              {!zipFile && (
                <div {...getTxtRootProps()} className={`dropzone secondary ${isTxtDragActive ? 'active' : ''} ${txtFile ? 'has-file' : ''}`}>
                  <input {...getTxtInputProps()} />

                  {txtFile ? (
                    <div className="file-preview">
                      <FileText size={28} className="file-icon" />
                      <p className="file-name">{txtFile.name}</p>
                      <p className="file-size">{formatFileSize(txtFile.size)}</p>
                      <button className="change-file" onClick={(e) => { e.stopPropagation(); setTxtFile(null); }}>Ändern</button>
                    </div>
                  ) : (
                    <div className="dropzone-secondary-inner" onClick={() => txtInputRef.current?.click()}>
                      <FileText size={22} className="dropzone-icon small" />
                      <div>
                        <p className="dropzone-text small">.txt-Datei hochladen</p>
                        <p className="dropzone-subtext small">Ohne Bilder &amp; Videos</p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {txtFile && !zipFile && (
                <div className="info-box">
                  <Info size={20} />
                  <p><strong>Nur Text-Export:</strong> Bilder, Videos und andere Medien werden nicht im PDF angezeigt.</p>
                </div>
              )}

              {(zipFile || txtFile) && (
                <div className="advanced-section">
                  <button className="advanced-toggle" onClick={() => setShowAdvanced(!showAdvanced)}>
                    {showAdvanced ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    Erweiterte Einstellungen
                  </button>
                  {showAdvanced && (
                    <div className="media-options">
                      <h3>Medien-Anzeige:</h3>
                      <div className="radio-group">
                        <label className={`radio-label ${mediaMode === 'both' ? 'selected' : ''}`}>
                          <input type="radio" name="mediaMode" value="both" checked={mediaMode === 'both'} onChange={(e) => setMediaMode(e.target.value as MediaMode)} disabled={!zipFile} />
                          <span className="radio-title">Thumbnail + Link</span>
                          <span className="radio-desc">Bildvorschau mit Dateiname</span>
                        </label>

                        <label className={`radio-label ${mediaMode === 'thumbnail' ? 'selected' : ''}`}>
                          <input type="radio" name="mediaMode" value="thumbnail" checked={mediaMode === 'thumbnail'} onChange={(e) => setMediaMode(e.target.value as MediaMode)} disabled={!zipFile} />
                          <span className="radio-title">Nur Thumbnails</span>
                          <span className="radio-desc">Kleine Bildvorschau</span>
                        </label>

                        <label className={`radio-label ${mediaMode === 'link' ? 'selected' : ''}`}>
                          <input type="radio" name="mediaMode" value="link" checked={mediaMode === 'link'} onChange={(e) => setMediaMode(e.target.value as MediaMode)} disabled={!zipFile} />
                          <span className="radio-title">Nur Links</span>
                          <span className="radio-desc">Nur Dateiname anzeigen</span>
                        </label>

                        <label className={`radio-label ${mediaMode === 'text_only' ? 'selected' : ''}`}>
                          <input type="radio" name="mediaMode" value="text_only" checked={mediaMode === 'text_only'} onChange={(e) => setMediaMode(e.target.value as MediaMode)} />
                          <span className="radio-title">Nur Text</span>
                          <span className="radio-desc">Keine Medien im PDF</span>
                        </label>
                      </div>
                      {!zipFile && <p className="media-disabled-hint">Medien-Optionen nur bei ZIP-Upload verfügbar</p>}
                    </div>
                  )}
                </div>
              )}

              {(zipFile || txtFile) && (
                <button className="upload-button" onClick={handleUpload}>
                  <Upload size={20} />
                  {zipFile ? 'ZIP hochladen & PDF erstellen' : 'Chat hochladen & PDF erstellen'}
                </button>
              )}
            </div>

            {platform === 'desktop' && (
              <CodeEntry onLoaded={handleHandoffLoaded} />
            )}
          </div>

          {platform === 'desktop' && (
            <div className="android-hint">
              <span className="android-hint__icon">📱</span>
              <span>
                <strong>Auf Android?</strong> WhatsApp-Exporte lassen sich nur per App direkt teilen —{' '}
                <a href="https://play.google.com/store/apps/details?id=de.chatarchiv" target="_blank" rel="noopener noreferrer">
                  ChatArchiv App kostenlos installieren →
                </a>
              </span>
            </div>
          )}
        </section>
      ) : (
        /* ── Non-idle states (uploading / processing / completed / error) ── */
        <main className="main">
          {uploadStatus.status === 'uploading' && (
            <div className="status-box">
              <Loader2 className="status-icon spinning" size={48} />
              <p className="status-text">{uploadStatus.message}</p>
              <div className="progress-bar"><div className="progress-fill" style={{ width: `${uploadStatus.progress}%` }} /></div>
            </div>
          )}

          {uploadStatus.status === 'processing' && (
            <div className="status-box">
              <Loader2 className="status-icon spinning" size={48} />
              <p className="status-text">{uploadStatus.message}</p>
              <div className="progress-bar"><div className="progress-fill" style={{ width: `${uploadStatus.progress}%` }} /></div>
              <p className="status-hint">Dies kann einen Moment dauern...</p>
            </div>
          )}

          {uploadStatus.status === 'completed' && !showPreview && (
            <div className="result-card">

              {/* ── Header ── */}
              <div className="result-card-header">
                <CheckCircle size={26} className="result-check-icon" />
                <div>
                  <h2 className="result-title">Chat erfolgreich erkannt!</h2>
                  <p className="result-subtitle">Wähle jetzt wie es weitergehen soll</p>
                </div>
              </div>

              {handoffSource && (
                <div className="handoff-badge">
                  <span>📱 Von App geladen</span>
                  {handoffExpiresIn !== null && (
                    <span className="handoff-badge__expiry">
                      {' '}· Code gültig noch {Math.round(handoffExpiresIn / 3600)}h
                    </span>
                  )}
                </div>
              )}

              {/* ── Stats ── */}
              {uploadStatus.result && (
                <div className="result-stats">
                  <div className="result-stat">
                    <MessageSquare size={15} />
                    <span>{uploadStatus.result.message_count.toLocaleString()} Nachrichten</span>
                  </div>
                  <div className="result-stat">
                    <Users size={15} />
                    <span>{uploadStatus.result.participants.join(', ')}</span>
                  </div>
                  {uploadStatus.result.has_media && (
                    <div className="result-stat">
                      <Film size={15} />
                      <span>{uploadStatus.result.media_count} Medien</span>
                    </div>
                  )}
                </div>
              )}

              {/* ── Primary CTA: Preview ── */}
              <div className="result-primary">
                <button className="result-preview-btn" onClick={loadPreview}>
                  {isPreviewLoading
                    ? <><Loader2 size={20} className="spinning" /> Vorschau wird geladen...</>
                    : <><Eye size={20} /> Vorschau öffnen &amp; anpassen</>
                  }
                </button>
                <p className="result-preview-hint">
                  Kostenlos · Farben, Layout und mehr anpassen · erst dann kaufen
                </p>
              </div>

              {/* ── Free PDF download ── */}
              <button
                className="result-free-btn"
                onClick={handleFreePdfDownload}
                disabled={isGeneratingFreePdf}
              >
                {isGeneratingFreePdf
                  ? <><Loader2 size={18} className="spinning" /> PDF wird erstellt...</>
                  : <><Download size={18} /> Kostenlos herunterladen (mit Wasserzeichen)</>
                }
              </button>

              {/* ── Divider ── */}
              <div className="result-divider">
                <span>oder direkt kaufen (Standard-Einstellungen)</span>
              </div>

              {/* ── Checkboxes (above buy button) ── */}
              <div className={`result-checkboxes ${checkboxError ? 'checkbox-shake' : ''}`}>
                <label className="widerruf-checkbox">
                  <input type="checkbox" checked={agbCheckbox} onChange={e => { setAgbCheckbox(e.target.checked); setCheckboxError(false) }} />
                  <span>
                    Ich habe die <a href="/agb.html" target="_blank" rel="noopener">AGB</a> und{' '}
                    <a href="/datenschutz.html" target="_blank" rel="noopener">Datenschutzerklärung</a> gelesen und akzeptiere diese.
                  </span>
                </label>
                <label className="widerruf-checkbox">
                  <input type="checkbox" checked={widerrufsCheckbox} onChange={e => { setWiderrufsCheckbox(e.target.checked); setCheckboxError(false) }} />
                  <span>
                    Ich stimme zu, dass mit der PDF-Erstellung sofort begonnen wird und verliere damit mein Widerrufsrecht.
                  </span>
                </label>
                {checkboxError && (
                  <p className="checkbox-error-hint">Bitte beide Felder bestätigen um fortzufahren</p>
                )}
              </div>

              {/* ── Direct buy ── */}
              <button
                className="result-buy-btn"
                disabled={isRendering}
                onClick={() => {
                  if (!agbCheckbox || !widerrufsCheckbox) {
                    setCheckboxError(true)
                    return
                  }
                  startCheckout()
                }}
              >
                {isRendering
                  ? <><Loader2 size={20} className="spinning" /> PDF wird erstellt...</>
                  : <><Download size={20} /> Direkt kaufen — €4,99</>
                }
              </button>

              {/* ── Reset ── */}
              <button className="retry-button secondary" onClick={resetUpload}>
                ↩ Anderen Chat hochladen
              </button>
            </div>
          )}

          {uploadStatus.status === 'error' && (
            <div className="status-box error">
              <AlertCircle className="status-icon" size={48} />
              <p className="status-text">{uploadStatus.message}</p>
              {uploadStatus.error && <p className="error-details">{uploadStatus.error}</p>}
              <button className="retry-button" onClick={resetUpload}>Erneut versuchen</button>
            </div>
          )}
        </main>
      )}

      {/* ── Landing sections below upload (idle only) ── */}
      {uploadStatus.status === 'idle' && (
        <>
          <LandingFeatures />
          <LandingPricing onStart={scrollToUpload} />
          <LandingFAQ />
        </>
      )}

      {/* ── Preview — fullscreen fixed overlay ── */}
      {showPreview && (
        <div className="preview-container">
          <div className="preview-header">
            <h2>Vorschau</h2>
            <div className="preview-actions">
              <button className="close-preview" onClick={() => { setShowPreview(false); setMobileSheetOpen(false); }}>
                Zurück
              </button>
              {!isMobile && (
                <>
                  <button
                    className="download-button-free"
                    onClick={handleFreePdfDownload}
                    disabled={isGeneratingFreePdf}
                  >
                    {isGeneratingFreePdf
                      ? <><Loader2 size={16} className="spinning" /> Wird erstellt...</>
                      : <><Download size={16} /> Kostenlos (Wasserzeichen)</>
                    }
                  </button>
                  <button className="download-button" onClick={startCheckout} disabled={isRendering || !widerrufsCheckbox || !agbCheckbox}
                    title={(!agbCheckbox || !widerrufsCheckbox) ? 'Bitte AGB und Widerrufsrecht in der Seitenleiste bestätigen' : undefined}>
                    {isRendering ? <><Loader2 size={20} className="spinning" /> PDF wird erstellt...</> : <><Download size={20} /> Vollständiges PDF — €4,99</>}
                  </button>
                </>
              )}
            </div>
          </div>

          {isPreviewLoading ? (
            <div className="preview-loading">
              <Loader2 className="spinning" size={48} />
              <p>Vorschau wird geladen...</p>
            </div>
          ) : isMobile ? (
            /* ── Mobile: fullscreen preview + bottom sheet ── */
            <>
              <div className="mobile-preview-scroll">
                <ParseQualityNotice quality={previewQuality} />
                <ChatPreview
                  messages={previewMessages}
                  options={previewOptions}
                  stats={previewStats}
                  isFreeVersion={true}
                />
              </div>
              {/* Mobile bottom bar */}
              <div className="mobile-preview-bar">
                <button className="mpb-options-btn" onClick={() => setMobileSheetOpen(true)}>
                  <Settings size={18} />
                  Optionen &amp; Kaufen
                </button>
              </div>
              <MobileOptionsSheet
                isOpen={mobileSheetOpen}
                onClose={() => setMobileSheetOpen(false)}
                options={previewOptions}
                setOptions={setPreviewOptions}
                stats={previewStats}
                agbCheckbox={agbCheckbox}
                setAgbCheckbox={setAgbCheckbox}
                widerrufsCheckbox={widerrufsCheckbox}
                setWiderrufsCheckbox={setWiderrufsCheckbox}
                onCheckout={startCheckout}
                isRendering={isRendering}
                onFreePdf={handleFreePdfDownload}
                isGeneratingFreePdf={isGeneratingFreePdf}
              />
            </>
          ) : (
            /* ── Desktop: sidebar + preview ── */
            <div className="preview-content">
              <PreviewSidebar
                options={previewOptions}
                setOptions={setPreviewOptions}
                stats={previewStats}
                agbCheckbox={agbCheckbox}
                setAgbCheckbox={setAgbCheckbox}
                widerrufsCheckbox={widerrufsCheckbox}
                setWiderrufsCheckbox={setWiderrufsCheckbox}
                onCheckout={startCheckout}
                isRendering={isRendering}
                onFreePdf={handleFreePdfDownload}
                isGeneratingFreePdf={isGeneratingFreePdf}
              />
              <div className="preview-main">
                <ParseQualityNotice quality={previewQuality} />
                <ChatPreview
                  messages={previewMessages}
                  options={previewOptions}
                  stats={previewStats}
                  isFreeVersion={true}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Payment Success overlay ── */}
      {paymentSuccessJobId && (
        <PaymentSuccess
          renderJobId={paymentSuccessJobId}
          onNewExport={() => {
            setPaymentSuccessJobId(null)
            resetUpload()
          }}
        />
      )}

      <LandingFooter />
        </>
      )}
    </div>
  )
}

export default App
